import React, { useEffect, useState } from 'react';
import { 
  CurrencyDollarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  Square3Stack3DIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';

export default function Subscriptions() {
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState<any>(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        validity_months: 1,
        credits: '',
        features: '',
        is_active: true,
    });

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = () => {
        setLoading(true);
        apiClient.get('/admin/plans')
        .then(({ data }) => {
            setPlans(data || []);
            setLoading(false);
        })
        .catch(err => {
            console.error(err);
            setLoading(false);
        });
    };

    const handleOpenModal = (plan: any = null) => {
        if (plan) {
            setEditingPlan(plan);
            const validityDays = plan.validity_days ?? 30;
            const validityMonths = Math.round(validityDays / 30);
            setFormData({
                name: plan.name,
                description: plan.description || '',
                price: String(plan.price_monthly ?? ''),
                validity_months: validityMonths,
                credits: String(plan.credits ?? ''),
                features: (plan.features ?? []).join('\n'),
                is_active: plan.is_active ?? true,
            });
        } else {
            setEditingPlan(null);
            setFormData({
                name: '',
                description: '',
                price: '',
                validity_months: 1,
                credits: '',
                features: '',
                is_active: true,
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        const payload = {
            name: formData.name,
            description: formData.description,
            price_monthly: parseFloat(formData.price) || 0,
            credits: parseInt(formData.credits, 10) || 0,
            validity_days: formData.validity_months * 30,
            features: formData.features
                .split('\n')
                .map(f => f.trim())
                .filter(f => f.length > 0),
            is_active: formData.is_active,
        };
        const url = editingPlan
            ? `/admin/plans/${editingPlan.id}`
            : '/admin/plans';
        const request = editingPlan
            ? apiClient.put(url, payload)
            : apiClient.post(url, payload);

        request
        .then(() => {
            setIsModalOpen(false);
            fetchPlans();
        })
        .catch(err => console.error(err));
    };

    const handleDelete = (id: string) => {
        if (!window.confirm("Are you sure you want to delete this plan?")) return;
        apiClient.delete(`/admin/plans/${id}`)
        .then(() => fetchPlans())
        .catch(err => console.error(err));
    };

    return (
        <div className="px-4 sm:px-8 py-4 sm:py-8 max-w-[1400px] mx-auto flex flex-col gap-6 sm:gap-8 pb-20">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-[36px] font-extrabold text-slate-900 tracking-tight">Subscription Plans</h1>
                    <p className="text-sm sm:text-[15px] text-slate-500 mt-1 sm:mt-2 font-medium">Manage tier pricing, features, and quotas.</p>
                </div>
                <button 
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-2 bg-brand-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-700 transition-colors shadow-sm shadow-brand-500/20 shrink-0"
                >
                    <PlusIcon className="w-5 h-5" /> Create New Plan
                </button>
            </div>

            {/* Plans List */}
            <div className="grid grid-cols-1 gap-4">
                {loading ? (
                    <div className="text-center py-10 font-bold text-slate-500">Loading plans...</div>
                ) : plans.length === 0 ? (
                    <div className="text-center py-10 font-bold text-slate-500">No plans found. Create one above.</div>
                ) : (
                    plans.map((plan: any) => (
                        <div key={plan.id} className="bg-white border border-slate-200 rounded-[20px] p-6 flex flex-col md:flex-row md:items-center justify-between shadow-sm hover:border-slate-300 transition-colors gap-6">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 bg-slate-100 text-slate-500 rounded-xl flex items-center justify-center shrink-0">
                                    <Square3Stack3DIcon className="w-7 h-7" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <h3 className="text-[18px] font-bold text-slate-900 mb-1">{plan.name}</h3>
                                    <p className="text-[13px] text-slate-500 font-medium leading-snug max-w-md">{plan.description}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-8 md:ml-auto">
                                <div className="text-right">
                                    <p className="text-[18px] font-extrabold text-slate-900">${plan.price_monthly}</p>
                                    <p className="text-[11px] font-extrabold text-brand-600 uppercase tracking-widest mt-1">{plan.credits} Credits</p>
                                    <p className="text-[11px] font-medium text-slate-400 mt-0.5">{plan.validity_days ?? 30}d validity</p>
                                    {!plan.is_active && (
                                        <span className="text-[10px] font-bold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full uppercase tracking-wide inline-block mt-1">
                                            Inactive
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <button 
                                        onClick={() => handleOpenModal(plan)}
                                        className="flex items-center justify-center gap-2 bg-slate-100 text-slate-600 px-4 py-2 rounded-lg text-[12px] font-bold hover:bg-slate-200 transition-colors"
                                    >
                                        <PencilIcon className="w-4 h-4" /> Edit
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(plan.id)}
                                        className="flex items-center justify-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-[12px] font-bold hover:bg-red-100 transition-colors"
                                    >
                                        <TrashIcon className="w-4 h-4" /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl p-5 sm:p-8 max-w-lg w-full relative shadow-xl">
                        <button 
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-6 right-6 text-slate-400 hover:text-slate-900 font-bold"
                        >
                            Close
                        </button>
                        <h2 className="text-2xl font-extrabold text-slate-900 mb-6">{editingPlan ? 'Edit Plan' : 'Create Plan'}</h2>
                        
                        <form onSubmit={handleSave} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Name</label>
                                <input 
                                    type="text" 
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Description</label>
                                <textarea 
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 min-h-[80px]"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Price ($)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.price}
                                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                                        className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                        required
                                        placeholder="Fixed price for plan duration"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Duration (Months)</label>
                                    <select
                                        value={formData.validity_months}
                                        onChange={(e) => setFormData({...formData, validity_months: parseInt(e.target.value) || 1})}
                                        className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                        required
                                    >
                                        {[1, 2, 3, 6, 12].map(month => (
                                            <option key={month} value={month}>{month} Month{month > 1 ? 's' : ''}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Credits Included</label>
                                <input
                                    type="number"
                                    value={formData.credits}
                                    onChange={(e) => setFormData({...formData, credits: e.target.value})}
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Features <span className="text-slate-400 font-normal">(one per line)</span></label>
                                <textarea
                                    value={formData.features}
                                    onChange={(e) => setFormData({...formData, features: e.target.value})}
                                    placeholder={"Real-time AI Extraction\nCSV Export Access\nPriority Support"}
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 min-h-[80px] font-mono text-sm"
                                />
                            </div>
                            <div className="flex items-center justify-between pt-2">
                                <div>
                                    <p className="text-sm font-bold text-slate-700">Active</p>
                                    <p className="text-xs text-slate-400">Inactive plans are hidden from users</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData({...formData, is_active: !formData.is_active})}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        formData.is_active ? 'bg-brand-600' : 'bg-slate-200'
                                    }`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                        formData.is_active ? 'translate-x-6' : 'translate-x-1'
                                    }`} />
                                </button>
                            </div>
                            <button
                                type="submit"
                                className="w-full mt-4 bg-brand-600 text-white font-bold py-3 rounded-xl hover:bg-brand-700 transition-colors"
                            >
                                {editingPlan ? 'Save Changes' : 'Create Plan'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}